import React, { useState, useEffect } from "react";
import "./login.css";
import logo from "../../utilities/gg-logo.png";
import { Alert, Snackbar } from "@mui/material";
import { useTheme } from "../../context/ThemeContext";

// The backend origin, named once. It was inline in the login fetch; the reset flow needs
// it three more times and three more copies of a hostname is three chances to fix two of
// them later.
const API =
  process.env.REACT_APP_API_URL || "https://hr-backend-72v1.onrender.com";

/* ---- Editorial line icons (1.6–1.7px stroke, currentColor) ---- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const IconMail = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" strokeWidth="1.6" {...stroke}>
    <rect x="2.5" y="4.5" width="15" height="11" rx="2.2"></rect>
    <path d="M3.2 5.5L10 10.5l6.8-5"></path>
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" strokeWidth="1.6" {...stroke}>
    <rect x="4" y="9" width="12" height="8" rx="2"></rect>
    <path d="M7 9V6.6a3 3 0 0 1 6 0V9"></path>
  </svg>
);
const IconEyeOpen = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" strokeWidth="1.6" {...stroke}>
    <path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z"></path>
    <circle cx="10" cy="10" r="2.3"></circle>
  </svg>
);
const IconEyeOff = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" strokeWidth="1.6" {...stroke}>
    <path d="M2 2l16 16"></path>
    <path d="M7.7 7.8A2.5 2.5 0 0 0 10 12.4M4.2 5.6C2.7 6.7 2 10 2 10s3 5 8 5a8.6 8.6 0 0 0 3.6-.8M9 5.1A8.7 8.7 0 0 1 10 5c5 0 8 5 8 5a13 13 0 0 1-1.7 2.3"></path>
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" strokeWidth="2" {...stroke}>
    <path d="M4 10h11M11 6l4 4-4 4"></path>
  </svg>
);
const IconSun = () => (
  <svg viewBox="0 0 20 20" width="19" height="19" strokeWidth="1.7" {...stroke}>
    <circle cx="10" cy="10" r="3.6"></circle>
    <path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4 4l1.4 1.4M14.6 14.6L16 16M16 4l-1.4 1.4M5.4 14.6L4 16"></path>
  </svg>
);
const IconMoon = () => (
  <svg viewBox="0 0 20 20" width="19" height="19" strokeWidth="1.7" {...stroke}>
    <path d="M16.5 11.8A6.8 6.8 0 1 1 8.2 3.5 5.3 5.3 0 0 0 16.5 11.8Z"></path>
  </svg>
);

const Login = (props) => {
  const [user, setUser] = useState(null);

  // States
  const [pass, setPass] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  // Editorial UI state
  const [showPassword, setShowPassword] = useState(false);

  // Forgot-password lives in this component rather than its own route, so it inherits the
  // brand, the theme toggle, the snackbar and every class in login.css. Four panels, one
  // shell: "signin" -> "forgot" -> "code" -> "reset".
  const [mode, setMode] = useState("signin");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  // Leaving a half-finished reset behind would let a stale code or token be submitted
  // against a different address than the one on screen.
  const goMode = (next, keepNotice = "") => {
    setMode(next);
    setMsg("");
    setNotice(keepNotice);
    if (next === "signin" || next === "forgot") {
      setCode("");
      setResetToken("");
      setNewPass("");
      setConfirmPass("");
    }
  };

  const call = async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  };

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) return setMsg("*Please enter your email*");
    setBusy(true);
    setMsg("");
    const { ok, data } = await call("/password/forgot", { email });
    setBusy(false);
    if (!ok) return setMsg(data.message || "Could not send a code. Try again.");
    // The reply is deliberately identical whether or not the address exists, so this
    // screen must not imply that it does.
    goMode(
      "code",
      data.message || "If that email belongs to a Growth Guild account, a code is on its way."
    );
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return setMsg("*Enter the 6-digit code*");
    setBusy(true);
    setMsg("");
    const { ok, data } = await call("/password/verify", { email, code: code.trim() });
    setBusy(false);
    if (!ok) return setMsg(data.message || "That code did not work.");
    goMode("reset");
    setResetToken(data.resetToken); // after goMode: it clears the token only on signin/forgot
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 8) return setMsg("*Password must be at least 8 characters*");
    if (newPass !== confirmPass) return setMsg("*Both passwords must match*");
    setBusy(true);
    setMsg("");
    const { ok, data } = await call("/password/reset", { resetToken, newPassword: newPass });
    setBusy(false);
    if (!ok) return setMsg(data.message || "Could not set that password.");
    setPass("");
    goMode("signin");
    setAlertMessage("Password updated. Sign in with your new password.");
    setShowAlert(true);
  };

  const { theme, toggleTheme } = useTheme();

  const handlePass = (e) => {
    setPass(e.target.value);
  };

  const handleEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (email === "") {
      setMsg("*Please Enter Email*");
      return;
    }
    if (pass === "") {
      setMsg("*Please Enter Your Password*");
      return;
    }

    // Authenticate by email + password only — the backend reads the account's
    // role from the database. The JWT is returned in JSON; App routes by role.
    try {
      const response = await fetch(`${API}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, pass }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.token) {
        localStorage.setItem("userValue", data.token);
        window.location.assign("/");
      } else {
        setAlertMessage(data.message || "Invalid credentials. Please try again.");
        setShowAlert(true);
      }
    } catch (err) {
      setAlertMessage("Could not reach the server. Please try again.");
      setShowAlert(true);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error) {
      setAlertMessage(error);
      setShowAlert(true);
    }
  }, []);

  return (
    <main className="gg-login">
      <div className="gg-login__glow"></div>
      <img className="gg-login__watermark" src={logo} alt="" aria-hidden="true" />

      <button
        type="button"
        className="gg-login__toggle"
        onClick={toggleTheme}
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <IconSun /> : <IconMoon />}
      </button>

      <div className="gg-login__col">
        <div className="gg-login__brand">
          <img src={logo} alt="Growth Guild" />
          <div>
            <div className="gg-login__brand-name">GROWTH</div>
            <div className="gg-login__brand-sub">GUILD HRMS</div>
          </div>
        </div>

        <h1 className="gg-login__title">
          {mode === "signin" ? "Welcome back" : mode === "reset" ? "Choose a new password" : "Reset your password"}
        </h1>
        <p className="gg-login__subtitle">
          {mode === "signin"
            ? "Sign in to your Growth Guild workspace."
            : mode === "forgot"
            ? "We'll email you a 6-digit code."
            : mode === "code"
            ? `Enter the code we sent to ${email}.`
            : "It needs to be at least 8 characters."}
        </p>

        {mode === "signin" ? (
        <form className="gg-login__form" onSubmit={handleLogin}>
          {/* Email */}
          <div className="gg-login__field">
            <label className="gg-login__label" htmlFor="gg-login-email">
              Email
            </label>
            <div className="gg-login__control">
              <span className="gg-login__lead">
                <IconMail />
              </span>
              <input
                id="gg-login-email"
                className="gg-login__input"
                type="email"
                placeholder="you@growthguild.com"
                autoComplete="email"
                value={email}
                onChange={handleEmail}
              />
            </div>
          </div>

          {/* Password */}
          <div className="gg-login__field">
            <label className="gg-login__label" htmlFor="gg-login-password">
              Password
            </label>
            <div className="gg-login__control">
              <span className="gg-login__lead">
                <IconLock />
              </span>
              <input
                id="gg-login-password"
                className="gg-login__input gg-login__input--pw"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
                value={pass}
                onChange={handlePass}
              />
              <button
                type="button"
                className="gg-login__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <IconEyeOff /> : <IconEyeOpen />}
              </button>
            </div>
          </div>

          {msg && (
            <p className="gg-login__error" role="alert">
              {msg}
            </p>
          )}

          <button type="submit" className="gg-login__btn">
            <span>Sign In</span>
            <IconArrow />
          </button>

          <button type="button" className="gg-login__link" onClick={() => goMode("forgot")}>
            Forgot your password?
          </button>
        </form>
        ) : mode === "forgot" ? (
          <form className="gg-login__form" onSubmit={requestCode}>
            <div className="gg-login__field">
              <label className="gg-login__label" htmlFor="gg-reset-email">Email</label>
              <div className="gg-login__control">
                <span className="gg-login__lead"><IconMail /></span>
                <input
                  id="gg-reset-email"
                  className="gg-login__input"
                  type="email"
                  placeholder="you@growthguild.com"
                  autoComplete="email"
                  value={email}
                  onChange={handleEmail}
                />
              </div>
            </div>

            {msg && <p className="gg-login__error" role="alert">{msg}</p>}

            <button type="submit" className="gg-login__btn" disabled={busy}>
              <span>{busy ? "Sending…" : "Send code"}</span>
              <IconArrow />
            </button>
            <button type="button" className="gg-login__link" onClick={() => goMode("signin")}>
              Back to sign in
            </button>
          </form>
        ) : mode === "code" ? (
          <form className="gg-login__form" onSubmit={verifyCode}>
            {notice && <p className="gg-login__notice">{notice}</p>}

            <div className="gg-login__field">
              <label className="gg-login__label" htmlFor="gg-reset-code">6-digit code</label>
              <div className="gg-login__control">
                <span className="gg-login__lead"><IconLock /></span>
                <input
                  id="gg-reset-code"
                  className="gg-login__input gg-login__input--code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </div>

            {msg && <p className="gg-login__error" role="alert">{msg}</p>}

            <button type="submit" className="gg-login__btn" disabled={busy}>
              <span>{busy ? "Checking…" : "Verify code"}</span>
              <IconArrow />
            </button>
            <button type="button" className="gg-login__link" onClick={() => goMode("forgot")}>
              Use a different email, or send another code
            </button>
          </form>
        ) : (
          <form className="gg-login__form" onSubmit={submitNewPassword}>
            <div className="gg-login__field">
              <label className="gg-login__label" htmlFor="gg-new-pass">New password</label>
              <div className="gg-login__control">
                <span className="gg-login__lead"><IconLock /></span>
                <input
                  id="gg-new-pass"
                  className="gg-login__input gg-login__input--pw"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                />
                <button
                  type="button"
                  className="gg-login__eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IconEyeOff /> : <IconEyeOpen />}
                </button>
              </div>
            </div>

            <div className="gg-login__field">
              <label className="gg-login__label" htmlFor="gg-confirm-pass">Confirm password</label>
              <div className="gg-login__control">
                <span className="gg-login__lead"><IconLock /></span>
                <input
                  id="gg-confirm-pass"
                  className="gg-login__input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Type it again"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                />
              </div>
            </div>

            {msg && <p className="gg-login__error" role="alert">{msg}</p>}

            <button type="submit" className="gg-login__btn" disabled={busy}>
              <span>{busy ? "Saving…" : "Set password"}</span>
              <IconArrow />
            </button>
            <button type="button" className="gg-login__link" onClick={() => goMode("signin")}>
              Cancel
            </button>
          </form>
        )}
      </div>

      <Snackbar
        open={showAlert}
        autoHideDuration={15000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setShowAlert(false)} severity="error">
          {alertMessage}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default Login;
